import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCQuery, invalidateQuery, FC } from '../../Services'
import { ConfirmNameDeleteDialog } from '../../Components/ConfirmNameDeleteDialog'
import { createNewAgency } from './Functions'
import { AgencyForm } from './AgencyForm'
import { ServerDialog } from './ServerDialog'
import { DataGrid } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import Collapse from '@mui/material/Collapse'
import BusinessIcon from '@mui/icons-material/Business'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/Edit'
import SettingsIcon from '@mui/icons-material/Settings'
import LaptopIcon from '@mui/icons-material/Laptop';
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

export const Agencies = () => {
  const { data: agencies = [], isSuccess: isSuccessAgencies } = useCQuery('agencies')
  const { data: servers = [], isSuccess: isSuccessServers } = useCQuery('servers')
  const navigate = useNavigate()

  const { username: loggedInUserName = '', role: loggedInUserRole = '' } =
    JSON.parse(window.localStorage?.getItem('user'))?.user
  const isSiapAdmin = ['siapAdmin', 'siapDeveloper'].includes(loggedInUserRole)
  const basePath = loggedInUserRole === 'siapDeveloper' ? '/siapdeveloper' : '/siapadmin'

  const [id, setId] = useState('')
  const [agencyName, setAgencyName] = useState('')
  const [codSociety, setCodSociety] = useState('')
  const [opUnit, setOpUnit] = useState('')
  const [serverId, setServerId] = useState('')

  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editDialogData, setEditDialogData] = useState(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const [deleteAgency, setDeleteAgency] = useState(null)
  const [isDeletingAgency, setIsDeletingAgency] = useState(false)

  const handleAgencyDetail = useCallback((agencyId) => {
    navigate(`${basePath}/agencies/${agencyId}`)
  }, [navigate, basePath])

  const handleDeleteAgency = useCallback(async () => {
    if (!deleteAgency?._id) return
    setIsDeletingAgency(true)
    try {
      await FC.service('agencies').remove(deleteAgency._id)
      window.growl.show({ severity: 'success', summary: 'Agenzia eliminata', detail: 'Eliminazione completata.' })
      setDeleteAgency(null)
      invalidateQuery('agencies')
      invalidateQuery('clients')
    } catch (err) {
      window.growl.show({ severity: 'error', summary: 'Eliminazione non riuscita', detail: err?.message || 'Errore' })
    } finally {
      setIsDeletingAgency(false)
    }
  }, [deleteAgency])

  const handleAgencyConfigs = useCallback((agencyId) => {
    navigate(`/siapadmin/configs/${agencyId}`)
  }, [navigate])

  const hasConfigAccess = loggedInUserRole === 'siapAdmin'

  const createAgency = useCallback(async () => {
    const isDuplicate = agencies.some((item) => item._id === id)

    if (!agencyName || isDuplicate) {
      window.growl.show({
        severity: 'error',
        summary: 'Errore',
        detail: isDuplicate ? 'Agenzia gia presente' : "Inserisci il nome dell'agenzia"
      })
      return
    }

    const newAgencyObj = {
      _id: id,
      agencyId: id,
      name: opUnit ? `${agencyName}_${opUnit}` : agencyName,
      opUnit,
      codSociety,
      clientNum: 0,
      serverId
    }

    const result = await createNewAgency(newAgencyObj, loggedInUserName)
    if (result) {
      invalidateQuery('agencies')
      invalidateQuery('configs')
      clearFields()
    }
  }, [id, agencyName, opUnit, codSociety, serverId, agencies, loggedInUserName])

  const clearFields = useCallback(() => {
    setId('')
    setAgencyName('')
    setCodSociety('')
    setOpUnit('')
    setServerId('')
  }, [])

  const filteredAgencies = useMemo(() => {
    if (!globalFilterValue) return agencies
    const lower = globalFilterValue.toLowerCase()
    return agencies.filter((a) =>
      Object.values(a).some((v) => String(v).toLowerCase().includes(lower))
    )
  }, [agencies, globalFilterValue])

  const handleEditServer = useCallback(async (serverData) => {
    if (!isSiapAdmin) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Non hai i permessi per modificare i dati.' })
      return
    }

    if (editDialogData?._id !== serverData?._id) {
      editDialogData && await FC.service('agencies').patch(editDialogData._id, { serverId: serverData?._id })
      invalidateQuery('agencies')
    }

    if (!serverData?.name || !serverData?.ip || !serverData?.atlanteURL || !serverData?.SFTPURL || !serverData?.usernameSFTP || !serverData?.passwordSFTP) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserisci tutti i campi.' })
      return
    }

    const { _id: sId, ...serverFields } = serverData || {}
    await FC.service('servers').patch(sId, serverFields)
    invalidateQuery('servers')
    setEditDialogOpen(false)
  }, [isSiapAdmin, editDialogData])

  const handleCreateServer = useCallback(async (serverData) => {
    if (!isSiapAdmin) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Non hai i permessi per modificare i dati.' })
      return
    }

    if (!serverData?.name || !serverData?.ip || !serverData?.atlanteURL || !serverData?.SFTPURL || !serverData?.usernameSFTP || !serverData?.passwordSFTP) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Inserisci tutti i campi.' })
      return
    }

    if (servers.some((item) => item.name.toUpperCase() === serverData?.name.toUpperCase() || item.ip === serverData?.ip)) {
      window.growl.show({ severity: 'error', summary: 'Errore', detail: 'Esiste gia un server con lo stesso nome ed IP.' })
      return
    }

    await FC.service('servers').create(serverData)
    invalidateQuery('servers')
    setCreateDialogOpen(false)
  }, [isSiapAdmin, servers])

  const handleSaveServer = useCallback((serverData, isCreateMode) => {
    if (isCreateMode) {
      handleCreateServer(serverData)
    } else {
      handleEditServer(serverData)
    }
  }, [handleCreateServer, handleEditServer])

  const columns = useMemo(() => [
    {
      field: '_id',
      headerName: 'ID',
      flex: 0.7,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={600} noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'name',
      headerName: 'Nome',
      flex: 1,
      minWidth: 140,
      sortable: true,
      renderCell: (params) => (
        <Typography variant='body2' fontWeight={500} noWrap>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'codSociety',
      headerName: 'Cod. Societa',
      flex: 0.7,
      minWidth: 110,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          size='small'
          variant='outlined'
          sx={{ fontWeight: 500, fontSize: 12 }}
        />
      )
    },
    {
      field: 'clientNum',
      headerName: 'Installazioni',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={String(params.value ?? 0)}
          size='small'
          color={params.value > 0 ? 'primary' : 'default'}
          variant='outlined'
          sx={{ fontWeight: 600, fontSize: 12, minWidth: 36 }}
        />
      )
    },
    {
      field: 'clientNumActive',
      headerName: 'Installazioni Online',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={String(params.value ?? 0)}
          size='small'
          color={params.value > 0 ? 'primary' : 'default'}
          variant='outlined'
          sx={{ fontWeight: 600, fontSize: 12, minWidth: 36 }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 200,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction='row' spacing={0.5} alignItems='center'>
          {isSiapAdmin && (
            <Tooltip
              title={
                (params.row.clientNum || 0) > 0
                  ? "Impossibile eliminare: l'agenzia ha ancora client registrati"
                  : 'Elimina agenzia'
              }
            >
              <span>
                <IconButton
                  size='small'
                  color='error'
                  disabled={(params.row.clientNum || 0) > 0}
                  onClick={() => (params.row.clientNum || 0) === 0 && setDeleteAgency(params.row)}
                  aria-label="Elimina agenzia"
                >
                  <DeleteOutlineIcon fontSize='small' />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title={isSiapAdmin ? 'Modifica server' : 'Visualizza server'}>
            <IconButton
              size='small'
              color='primary'
              onClick={() => { setEditDialogData(params.row); setEditDialogOpen(true) }}
              aria-label={`${isSiapAdmin ? 'Modifica' : 'Visualizza'} server per ${params.row.name}`}
            >
              {isSiapAdmin ? <EditIcon fontSize='small' /> : <VisibilityIcon fontSize='small' />}
            </IconButton>
          </Tooltip>
          {hasConfigAccess && (
            <Tooltip title='Configurazioni'>
              <IconButton
                size='small'
                color='primary'
                onClick={() => handleAgencyConfigs(params.row._id)}
                aria-label={`Configurazioni per ${params.row.name}`}
              >
                <SettingsIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Client'>
            <IconButton
              size='small'
              color='primary'
              onClick={() => handleAgencyDetail(params.row._id)}
              aria-label={`Client per ${params.row.name}`}
            >
              <LaptopIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ], [isSiapAdmin, hasConfigAccess, handleAgencyConfigs, handleAgencyDetail])

  if (!isSuccessAgencies || !isSuccessServers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>
            Caricamento agenzie...
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pb: 5 }}>
      <ConfirmNameDeleteDialog
        open={!!deleteAgency}
        onClose={() => !isDeletingAgency && setDeleteAgency(null)}
        title='Elimina agenzia'
        nameToMatch={deleteAgency?.name || ''}
        nameFieldLabel="Digita il nome dell'agenzia per confermare"
        hint="Per sicurezza, inserisci esattamente il nome dell'agenzia come in elenco (colonna Nome)."
        onConfirm={handleDeleteAgency}
        isProcessing={isDeletingAgency}
      />

      {/* Server Dialogs */}
      <ServerDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSaveServer}
        servers={servers}
        isSiapAdmin={isSiapAdmin}
        editData={editDialogData}
      />
      <ServerDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleSaveServer}
        servers={servers}
        isSiapAdmin={isSiapAdmin}
        editData={null}
      />

      {/* Page Header */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2, mt: 1 }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <BusinessIcon color='primary' sx={{ fontSize: 28 }} />
          <Typography variant='h5' fontWeight={700} color='text.primary'>
            Agenzie
          </Typography>
          <Chip
            label={`${agencies.length} agenzie`}
            size='small'
            variant='outlined'
            sx={{ fontWeight: 500 }}
          />
        </Stack>

        {isSiapAdmin && (
          <Button
            variant={showCreateForm ? 'outlined' : 'contained'}
            size='small'
            onClick={() => setShowCreateForm((prev) => !prev)}
            startIcon={showCreateForm ? <ExpandLessIcon /> : <AddIcon />}
            aria-label={showCreateForm ? 'Nascondi form creazione' : 'Mostra form creazione'}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {showCreateForm ? 'Chiudi' : 'Nuova Agenzia'}
          </Button>
        )}
      </Stack>

      {/* Create Form */}
      {isSiapAdmin && (
        <Collapse in={showCreateForm}>
          <Paper
            elevation={0}
            sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <AgencyForm
              id={id}
              agencyName={agencyName}
              opUnit={opUnit}
              codSociety={codSociety}
              serverId={serverId}
              setId={setId}
              setAgencyName={setAgencyName}
              setOpUnit={setOpUnit}
              setCodSociety={setCodSociety}
              setServerId={setServerId}
              serverOptions={servers.map((s) => ({ _id: s._id, name: s.name }))}
              createAgency={createAgency}
              clearFields={clearFields}
              onOpenNewServerDialog={() => setCreateDialogOpen(true)}
            />
          </Paper>
        </Collapse>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
      >
        {/* Table Toolbar */}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 1.5 }}
        >
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <SearchIcon fontSize='small' color='action' />
            <Typography variant='body2' fontWeight={600} color='text.secondary'>
              Ricerca
            </Typography>
          </Stack>
          <TextField
            size='small'
            value={globalFilterValue}
            onChange={(e) => setGlobalFilterValue(e.target.value)}
            placeholder='Cerca nelle agenzie...'
            aria-label='Ricerca agenzie'
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' color='action' />
                  </InputAdornment>
                )
              }
            }}
            sx={{ width: 300 }}
          />
        </Stack>

        {/* DataGrid */}
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={filteredAgencies}
            columns={columns}
            getRowId={(row) => row._id}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            localeText={{
              noRowsLabel: 'Nessuna agenzia trovata.',
              MuiTablePagination: {
                labelRowsPerPage: 'Righe per pagina:',
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} di ${count !== -1 ? count : `piu di ${to}`}`
              }
            }}
            sx={dataGridSx}
          />
        </Box>
      </Paper>
    </Box>
  )
}

const dataGridSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '--DataGrid-containerBackground': '#004680',
  '& .MuiDataGrid-columnHeader': {
    color: '#fff',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: 13,
    color: '#fff'
  },
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    color: '#fff'
  },
  '& .MuiDataGrid-sortIcon': {
    color: '#fff'
  },
  '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root': {
    color: '#fff'
  },
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
    color: 'text.primary',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' }
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: 'action.hover'
    }
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid',
    borderTopColor: 'divider',
    backgroundColor: 'transparent'
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none'
  }
}
